/**
 * StyleBuilderPanel — floating visual style editor popover with 6 tabs.
 * Renders via React.createPortal and positions itself relative to an anchor element.
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { StyleProperties } from './types';
import { ColorsTab } from './tabs/ColorsTab';
import { BackgroundTab } from './tabs/BackgroundTab';
import { TypographyTab } from './tabs/TypographyTab';
import { SpacingTab } from './tabs/SpacingTab';
import { BorderTab } from './tabs/BorderTab';
import { EffectsTab } from './tabs/EffectsTab';
import { LivePreview } from './LivePreview';
import { createPortal } from 'react-dom';

export interface StyleBuilderPanelProps {
  slot: string;
  slotLabel: string;
  value: StyleProperties;
  anchorEl: HTMLElement;
  onApply: (styles: StyleProperties) => void;
  onClose: () => void;
  isDark: boolean;
  /** Passed straight through to `BackgroundTab` (2026-08-30, wix-style-design Task 7) — see that
   *  component's own doc comment. Optional; every existing caller with none stays unaffected. */
  onBrowseAssets?: () => Promise<string>;
}

type TabId = 'colors' | 'background' | 'typography' | 'spacing' | 'border' | 'effects' | 'code';

const TABS: { id: TabId; label: string }[] = [
  { id: 'colors',     label: 'Colors' },
  { id: 'background', label: 'Background' },
  { id: 'typography', label: 'Typography' },
  { id: 'spacing',    label: 'Spacing' },
  { id: 'border',     label: 'Border' },
  { id: 'effects',    label: 'Effects' },
  { id: 'code',       label: '{ }' },
];

const PANEL_WIDTH = 320;

/**
 * StyleBuilderPanel — the main style editor popover.
 */
export const StyleBuilderPanel: React.FC<StyleBuilderPanelProps> = ({
  slot,
  slotLabel,
  value,
  anchorEl,
  onApply,
  onClose,
  isDark,
  onBrowseAssets,
}) => {
  const [localValue, setLocalValue] = useState<StyleProperties>({ ...value });
  const [activeTab, setActiveTab] = useState<TabId>('colors');
  const [codeText, setCodeText] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // ── Positioning ────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const panelHeight = 560; // approximate height, corrected below once rendered
    const margin = 8;

    let left = rect.right + margin;
    let top = rect.top;

    // Clamp right edge
    if (left + PANEL_WIDTH > window.innerWidth - margin) {
      // Try left side of anchor
      left = rect.left - PANEL_WIDTH - margin;
    }
    // Clamp left edge
    if (left < margin) {
      left = margin;
    }
    // Clamp bottom edge
    if (top + panelHeight > window.innerHeight - margin) {
      top = window.innerHeight - panelHeight - margin;
    }
    // Clamp top edge
    if (top < margin) {
      top = margin;
    }

    setPosition({ top, left });
  }, [anchorEl]);

  // ── Re-clamp using the panel's real rendered height ─────────────────────────
  // The 560px guess above doesn't match every tab/content combination (a large
  // font-size preview, a tall aspect-ratio preview, etc.), which can push the
  // footer below the viewport. A ResizeObserver catches every such size change
  // — any tab, any field — and shifts the panel upward (never above the top
  // margin) so the footer always stays on screen, within the visible canvas.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const margin = 8;

    const reclamp = () => {
      const actualHeight = el.getBoundingClientRect().height;
      const maxTop = Math.max(margin, window.innerHeight - actualHeight - margin);
      setPosition((prev) => (prev.top > maxTop ? { ...prev, top: maxTop } : prev));
    };

    reclamp();
    const observer = new ResizeObserver(reclamp);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Click outside ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // ── Keyboard (Escape) ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ── Sync code text when switching to code tab ─────────────────────────────
  useEffect(() => {
    if (activeTab === 'code') {
      // Strip keys with null/undefined values for cleaner display
      const clean = Object.fromEntries(
        Object.entries(localValue).filter(([, v]) => v !== undefined && v !== null && v !== '')
      );
      setCodeText(JSON.stringify(clean, null, 2));
      setCodeError(null);
    }
  }, [activeTab]); // intentionally only on tab change — not on every localValue change

  // ── Styles ────────────────────────────────────────────────────────────────

  const panelBg = isDark ? 'rgba(15,23,42,0.97)' : '#ffffff';
  const panelBorder = isDark ? 'rgba(51,65,85,0.6)' : '#e2e8f0';
  const headerBg = isDark ? 'rgba(2,6,23,0.8)' : '#f8fafc';
  const accent = isDark ? '#22c55e' : '#16a34a';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const tabBarBg = isDark ? 'rgba(2,6,23,0.6)' : '#f1f5f9';
  const dividerColor = isDark ? 'rgba(51,65,85,0.4)' : '#e2e8f0';
  const footerBg = isDark ? 'rgba(2,6,23,0.7)' : '#f8fafc';

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: position.top,
    left: position.left,
    width: PANEL_WIDTH,
    maxHeight: 'calc(100vh - 16px)',
    background: panelBg,
    border: `1px solid ${panelBorder}`,
    borderRadius: 10,
    boxShadow: isDark
      ? '0 20px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)'
      : '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)',
    zIndex: 1000001,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backdropFilter: 'blur(12px)',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    background: headerBg,
    borderBottom: `1px solid ${dividerColor}`,
    flexShrink: 0,
  };

  const headerLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: accent,
  };

  const closeBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: textSecondary,
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: 1,
    padding: '0 2px',
    display: 'flex',
    alignItems: 'center',
  };

  const tabBarStyle: React.CSSProperties = {
    display: 'flex',
    overflowX: 'auto',
    background: tabBarBg,
    borderBottom: `1px solid ${dividerColor}`,
    flexShrink: 0,
    scrollbarWidth: 'none',
  };

  const tabContentStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 14px',
    maxHeight: 360,
    scrollbarWidth: 'thin',
    scrollbarColor: `${panelBorder} transparent`,
  };

  const previewWrapperStyle: React.CSSProperties = {
    padding: '0 14px 8px',
    flexShrink: 0,
    borderTop: `1px solid ${dividerColor}`,
    maxHeight: 140,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: `${panelBorder} transparent`,
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    background: footerBg,
    borderTop: `1px solid ${dividerColor}`,
    flexShrink: 0,
  };

  const clearBtnStyle: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: 5,
    border: `1px solid ${panelBorder}`,
    background: 'transparent',
    color: textSecondary,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
  };

  const applyBtnStyle: React.CSSProperties = {
    padding: '6px 16px',
    borderRadius: 5,
    border: 'none',
    background: accent,
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
  };

  const tabBtnStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '7px 10px',
    border: 'none',
    background: 'transparent',
    color: isActive ? accent : textSecondary,
    cursor: 'pointer',
    fontSize: 10,
    fontWeight: isActive ? 700 : 400,
    borderBottom: isActive ? `2px solid ${accent}` : '2px solid transparent',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    letterSpacing: '0.04em',
    transition: 'color 0.15s, border-color 0.15s',
  });

  // ── Active Tab Renderer ───────────────────────────────────────────────────

  const renderTab = () => {
    const tabProps = {
      value: localValue,
      onChange: setLocalValue,
      isDark,
    };
    switch (activeTab) {
      case 'colors': return <ColorsTab {...tabProps} />;
      case 'background': return <BackgroundTab {...tabProps} onBrowseAssets={onBrowseAssets} />;
      case 'typography': return <TypographyTab {...tabProps} />;
      case 'spacing': return <SpacingTab {...tabProps} />;
      case 'border': return <BorderTab {...tabProps} />;
      case 'effects': return <EffectsTab {...tabProps} />;
      case 'code': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
          <div style={{ fontSize: 10, color: textSecondary, lineHeight: 1.5 }}>
            Edit the style object directly as JSON. Changes apply when you click&nbsp;<strong style={{ color: accent }}>Apply</strong>.
          </div>
          <textarea
            value={codeText}
            onChange={(e) => {
              const raw = e.target.value;
              setCodeText(raw);
              try {
                const parsed = JSON.parse(raw);
                if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                  setLocalValue(parsed as StyleProperties);
                  setCodeError(null);
                } else {
                  setCodeError('Value must be a JSON object { }');
                }
              } catch {
                setCodeError('Invalid JSON — keep editing…');
              }
            }}
            spellCheck={false}
            style={{
              flex: 1,
              minHeight: 260,
              resize: 'vertical',
              fontFamily: 'ui-monospace, "Cascadia Code", "Fira Code", Consolas, monospace',
              fontSize: 12,
              lineHeight: 1.6,
              padding: '10px 12px',
              borderRadius: 6,
              border: `1px solid ${codeError ? '#ef4444' : panelBorder}`,
              background: isDark ? 'rgba(2,6,23,0.7)' : '#f8fafc',
              color: isDark ? '#e2e8f0' : '#1e293b',
              outline: 'none',
              whiteSpace: 'pre',
              overflowWrap: 'normal',
              overflowX: 'auto',
              boxSizing: 'border-box',
              width: '100%',
              transition: 'border-color 0.15s',
            }}
          />
          {codeError && (
            <div style={{ fontSize: 11, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>⚠</span> {codeError}
            </div>
          )}
        </div>
      );
      default: return null;
    }
  };

  // ── Portal content ────────────────────────────────────────────────────────

  const content = (
    <div ref={panelRef} style={panelStyle} role="dialog" aria-label={`Style editor: ${slotLabel}`}>
      {/* Header */}
      <div style={headerStyle}>
        <span style={headerLabelStyle}>{slotLabel}</span>
        <button
          type="button"
          style={closeBtnStyle}
          onClick={onClose}
          aria-label="Close style editor"
        >
          ×
        </button>
      </div>

      {/* Tab bar */}
      <div style={tabBarStyle} role="tablist" aria-label="Style categories">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`style-tab-panel-${tab.id}`}
            style={tabBtnStyle(activeTab === tab.id)}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div
        id={`style-tab-panel-${activeTab}`}
        role="tabpanel"
        aria-label={activeTab}
        style={tabContentStyle}
      >
        {renderTab()}
      </div>

      {/* Live preview */}
      <div style={previewWrapperStyle}>
        <LivePreview slot={slot} styles={localValue} isDark={isDark} />
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <button
          type="button"
          style={clearBtnStyle}
          onClick={() => setLocalValue({})}
          aria-label="Clear all styles"
        >
          Clear All
        </button>
        <button
          type="button"
          style={applyBtnStyle}
          onClick={() => {
            onApply(localValue);
            onClose();
          }}
          aria-label="Apply styles"
        >
          Apply
        </button>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

StyleBuilderPanel.displayName = 'StyleBuilderPanel';
